'use client';

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations, useLocale } from 'next-intl';
import { useLocalizedField } from '@/hooks/useLocalizedField';
import HeaderPage from "@/components/header-page";
import FestivalCard from "@/components/festival-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup } from "@/components/ui/select";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Filter, X, ChevronDown, ChevronUp, Calendar, MapPin, Music, Star, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const metaData = {
  meta_title_ru: "Откройте лучшие фестивали в Тунисе — Культура, музыка и искусство",
  meta_description_ru: "Исследуйте самые интересные фестивали Туниса, сочетающие музыку, танцы, искусство и традиции. Примите участие в незабываемых культурных событиях по всей стране.",
  meta_keywords_ru: "фестивали в Тунисе, музыкальные фестивали Тунис, фестивали искусства Тунис, культурные события Тунис, традиционные фестивали Тунис, живая музыка Тунис, международные фестивали Тунис, события Тунис",
  meta_title_fr: "Découvrez les Meilleurs Festivals en Tunisie - Culture, Musique et Arts",
  meta_description_fr: "Explorez les festivals incontournables en Tunisie, offrant une combinaison unique de musique, danse, arts et traditions. Participez à des événements culturels inoubliables à travers le pays.",
  meta_keywords_fr: "festivals en Tunisie, festivals musique Tunisie, festivals arts Tunisie, événements culturels Tunisie, festivals traditionnels Tunisie, musique live Tunisie, festivals internationaux Tunisie, événements Tunisie",
  meta_title_en: "Discover the Best Festivals in Tunisia - Culture, Music, and Arts",
  meta_description_en: "Explore must-attend festivals in Tunisia, offering a unique blend of music, dance, arts, and traditions. Participate in unforgettable cultural events across the country.",
  meta_keywords_en: "festivals in Tunisia, music festivals Tunisia, arts festivals Tunisia, cultural events Tunisia, traditional festivals Tunisia, live music Tunisia, international festivals Tunisia, Tunisia events",
  meta_title_ar: "اكتشف أفضل المهرجانات في تونس - ثقافة، موسيقى وفنون",
  meta_description_ar: "اكتشف المهرجانات التي لا بد من حضورها في تونس، حيث تجمع بين الموسيقى، الرقص، الفنون والتقاليد. شارك في فعاليات ثقافية لا تُنسى في جميع أنحاء البلاد.",
  meta_keywords_ar: "مهرجانات تونس, مهرجانات موسيقى تونس, مهرجانات فنون تونس, فعاليات ثقافية تونس, مهرجانات تقليدية تونس, موسيقى حية تونس, مهرجانات دولية تونس, فعاليات تونس",
  meta_title_zh: "探索突尼斯最佳节庆活动 - 文化、音乐与艺术",
  meta_description_zh: "体验融合音乐、舞蹈、艺术和传统的精彩突尼斯节庆活动。参与遍布全国的难忘文化盛事。",
  meta_keywords_zh: "突尼斯节庆, 突尼斯音乐节, 突尼斯艺术节, 文化活动 突尼斯, 传统节庆 突尼斯, 现场音乐 突尼斯, 国际节庆 突尼斯, 突尼斯活动",
  meta_title_ko: "튀니지 최고의 축제 탐방 – 문화, 음악 및 예술",
  meta_description_ko: "음악, 댄스, 예술, 전통이 어우러진 튀니지의 대표 축제를 경험해 보세요. 전국 곳곳에서 열리는 잊지 못할 문화 이벤트에 참여해보세요.",
  meta_keywords_ko: "튀니지 축제, 튀니지 음악 페스티벌, 튀니지 예술 행사, 튀니지 문화 이벤트, 튀니지 전통 축제, 라이브 음악 튀니지, 국제 축제 튀니지, 튀니지 행사",
  meta_title_ja: "チュニジアのベストフェスティバルを体験 - 文化・音楽・芸術",
  meta_description_ja: "音楽、ダンス、芸術、伝統が融合したチュニジアの主要フェスティバルをご案内します。全国で開催される忘れられない文化的イベントに参加してみましょう。",
  meta_keywords_ja: "チュニジア フェスティバル, チュニジア 音楽祭, チュニジア 芸術イベント, チュニジア 文化的行事, チュニジア 伝統祭り, ライブ音楽 チュニジア, 国際フェスティバル チュニジア, チュニジア イベント"
};

interface PaginationLink {
  url: string | null;
  label: string;
  active: boolean;
}

interface FestivalApiResponse {
  festival: {
    current_page: number;
    data: any[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: PaginationLink[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
  };
}

interface FilterOptions {
  destinations: any[];
  categories?: any[];
}

export default function FestivalsClient() {
  const t = useTranslations();
  const locale = useLocale();
  const { getLocalizedField } = useLocalizedField();
  const router = useRouter();
  const searchParams = useSearchParams();
  
  // State for festivals data
  const [festivals, setFestivals] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<FestivalApiResponse['festival'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDestination, setSelectedDestination] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    destinations: [],
    categories: []
  });

  // Update SEO metadata on locale change
  useEffect(() => {
    const suffix = locale === 'fr' ? 'fr' : locale;
    document.title = metaData[`meta_title_${suffix}` as keyof typeof metaData] || metaData.meta_title_fr;
    
    const metaDesc = document.querySelector('meta[name="description"]');
    const description = metaData[`meta_description_${suffix}` as keyof typeof metaData] || metaData.meta_description_fr;
    if (metaDesc && description) metaDesc.setAttribute('content', description);
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const keywords = metaData[`meta_keywords_${suffix}` as keyof typeof metaData] || metaData.meta_keywords_fr;
    if (metaKeywords && keywords) metaKeywords.setAttribute('content', keywords);
  }, [locale]);

  // Build query parameters for API
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    
    if (searchQuery) params.append("search", searchQuery);
    if (selectedDestination !== "all") {
      params.append("destination_id", selectedDestination);
    }
    if (selectedCategory !== "all") {
      params.append("category_id", selectedCategory);
    }
    
    return params;
  };

  // Build URL parameters for UI
  const buildUrlParams = () => {
    const params = new URLSearchParams();
    
    if (currentPage !== 1) params.append("page", currentPage.toString());
    if (searchQuery) params.append("search", searchQuery);
    if (selectedDestination !== "all") params.append("destination", selectedDestination);
    if (selectedCategory !== "all") params.append("category", selectedCategory);
    
    return params;
  };

  // Update URL when filters change
  const updateUrl = () => {
    if (isInitialLoad) return;
    
    const params = buildUrlParams();
    const queryString = params.toString();
    const newUrl = queryString ? `/festivals?${queryString}` : '/festivals';
    
    router.push(newUrl, { scroll: false });
  };

  // Fetch filter options (destinations and categories)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch destinations
        const destinationsResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL}alldestinations`);
        const destinationsData = await destinationsResponse.json();
        
        const destinationsList: any[] = destinationsData.destinations || [];
        const sortedDestinations = destinationsList.sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        
        // Fetch festival categories if your API has this endpoint
        let categoriesList: any[] = [];
        try {
          const categoriesResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL}festival-categories`);
          const categoriesData = await categoriesResponse.json();
          categoriesList = categoriesData.categories || [];
        } catch (error) {
          console.log("No categories endpoint available");
        }
        
        setFilterOptions({
          destinations: sortedDestinations,
          categories: categoriesList
        });
        setLoadingFilters(false);
      } catch (error) {
        console.error("Error fetching filter options:", error);
        setLoadingFilters(false);
      }
    };
    
    fetchFilterOptions();
  }, []);

  // Initialize state from URL params
  useEffect(() => {
    if (!searchParams || !isInitialLoad) return;
    
    const pageParam = searchParams.get("page");
    const searchParam = searchParams.get("search");
    const destinationParam = searchParams.get("destination");
    const categoryParam = searchParams.get("category");
    
    if (pageParam) {
      const page = parseInt(pageParam);
      if (!isNaN(page) && page > 0) {
        setCurrentPage(page);
      }
    }
    
    if (searchParam) setSearchQuery(searchParam);
    if (destinationParam) setSelectedDestination(destinationParam);
    if (categoryParam) setSelectedCategory(categoryParam);
    
    setIsInitialLoad(false);
  }, [searchParams, isInitialLoad]);

  // Update URL when filters change
  useEffect(() => {
    updateUrl();
  }, [currentPage, searchQuery, selectedDestination, selectedCategory]);

  // Fetch festivals based on filters
  useEffect(() => {
    const fetchFestivals = async () => {
      if (isInitialLoad || loadingFilters) return;
      
      setLoading(true);
      try {
        const apiParams = buildQueryParams();
        
        // Fetch data from festivals API endpoint
        const apiUrl = `${process.env.NEXT_PUBLIC_API_URL}festival?${apiParams.toString()}`;
        const response = await fetch(apiUrl);
        const data: FestivalApiResponse = await response.json();
        
        setFestivals(data.festival.data);
        setPaginationData(data.festival);
      } catch (error) {
        console.error("Error fetching festivals:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFestivals();
  }, [currentPage, searchQuery, selectedDestination, selectedCategory, isInitialLoad, loadingFilters]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Handle filter changes
  const handleDestinationChange = (value: string) => {
    setSelectedDestination(value);
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Handle pagination
  const handlePageChange = (url: string | null) => {
    if (!url) return;
    
    try {
      const urlObj = new URL(url);
      const pageParam = urlObj.searchParams.get("page");
      if (pageParam) {
        const page = parseInt(pageParam);
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      console.error("Error parsing page URL:", error);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDestination("all");
    setSelectedCategory("all");
    setCurrentPage(1);
    setShowMobileFilters(false);
    
    router.push('/festivals', { scroll: false });
  };

  // Clear single filters
  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const clearDestination = () => {
    setSelectedDestination("all");
    setCurrentPage(1);
  };

  const clearCategory = () => {
    setSelectedCategory("all");
    setCurrentPage(1);
  };

  // Get selected names for display
  const getSelectedDestinationName = () => {
    if (selectedDestination === "all") return null;
    const destination = filterOptions.destinations.find(d => d.slug === selectedDestination);
    return destination ? destination.name : selectedDestination;
  };

  const getSelectedCategoryName = () => {
    if (selectedCategory === "all") return null;
    const category = filterOptions.categories?.find(c => c.slug === selectedCategory);
    return category ? category.name : selectedCategory;
  };

  // Function to render pagination links
  const renderPaginationLinks = () => {
    if (!paginationData || !paginationData.links) return null;

    return paginationData.links.map((link, index) => {
      if (link.label === "...") {
        return (
          <PaginationItem key={index}>
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      const isPrevious = link.label.includes("previous");
      const isNext = link.label.includes("next");
      
      if (isPrevious) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(paginationData?.prev_page_url || null);
              }}
              className={`gap-2 px-3 sm:px-4 py-2 min-w-[100px] min-h-[44px] flex items-center justify-center ${
                !paginationData?.prev_page_url
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {locale === "ar" ? (
                <>
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.previous")}
                  </span>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                </>
              ) : (
                <>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.previous")}
                  </span>
                </>
              )}
            </PaginationLink>
          </PaginationItem>
        );
      }

      if (isNext) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(paginationData?.next_page_url || null);
              }}
              className={`gap-2 px-3 sm:px-4 py-2 min-w-[100px] min-h-[44px] flex items-center justify-center ${
                !paginationData?.next_page_url
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {locale === "ar" ? (
                <>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.next")}
                  </span>
                </>
              ) : (
                <>
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.next")}
                  </span>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                </>
              )}
            </PaginationLink>
          </PaginationItem>
        );
      }

      const pageNum = parseInt(link.label);
      if (!isNaN(pageNum)) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(link.url);
              }}
              isActive={link.active}
            >
              {pageNum}
            </PaginationLink>
          </PaginationItem>
        );
      }

      return null;
    });
  };

  // Filter sidebar component
  const FilterSidebar = () => (
    <div className="bg-card rounded-xl p-6 shadow-sm border border-border space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Music className="h-5 w-5 text-purple-600" />
        </div>
        <h2 className="text-xl font-bold text-foreground">{t('festivals.filters')}</h2>
      </div>

      {/* Search Filter */}
      <div>
        <h3 className="text-lg font-semibold mb-2">{t('festivals.search')}</h3>
        <form onSubmit={handleSearch}>
          <div className="relative">
            <Input
              placeholder={t('festivals.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-3"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Destination Filter */}
      <div>
        <h3 className="text-lg font-semibold mb-2">{t('festivals.destination')}</h3>
        <Select
          value={selectedDestination}
          onValueChange={handleDestinationChange}
          disabled={loadingFilters}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('festivals.allDestinations')}>
              {selectedDestination !== "all"
                ? getSelectedDestinationName()
                : t('festivals.allDestinations')}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-[400px] overflow-y-auto">
            <SelectGroup>
              <SelectItem value="all" className="font-semibold py-3">
                {t('festivals.allDestinations')}
              </SelectItem>
              {filterOptions.destinations.map((destination) => (
                <SelectItem
                  key={destination.id}
                  value={destination.slug}
                  className="py-2 text-sm text-gray-600"
                >
                  {destination.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Category Filter (if available) */}
      {filterOptions.categories && filterOptions.categories.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-2">{t('festivals.category')}</h3>
          <Select
            value={selectedCategory}
            onValueChange={handleCategoryChange}
            disabled={loadingFilters}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('festivals.allCategories')}>
                {selectedCategory !== "all"
                  ? getSelectedCategoryName()
                  : t('festivals.allCategories')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all" className="font-semibold py-3">
                  {t('festivals.allCategories')}
                </SelectItem>
                {filterOptions.categories.map((category) => (
                  <SelectItem
                    key={category.id}
                    value={category.slug}
                    className="py-2 text-sm text-gray-600"
                  >
                    {category.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Active Filters */}
      {(selectedDestination !== "all" || selectedCategory !== "all" || searchQuery) && (
        <div className="pt-4 border-t space-y-4">
          <h3 className="text-lg font-semibold">{t('festivals.activeFilters')}</h3>
          <div className="space-y-2">
            {searchQuery && (
              <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-md">
                <span className="text-sm text-blue-700">
                  {t('festivals.search')}: "{searchQuery}"
                </span>
                <button
                  onClick={clearSearch}
                  className="text-blue-500 hover:text-blue-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {selectedDestination !== "all" && (
              <div className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-md">
                <span className="text-sm text-green-700">
                  {t('festivals.destination')}: {getSelectedDestinationName()}
                </span>
                <button
                  onClick={clearDestination}
                  className="text-green-500 hover:text-green-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {selectedCategory !== "all" && filterOptions.categories && (
              <div className="flex items-center justify-between bg-purple-50 px-3 py-2 rounded-md">
                <span className="text-sm text-purple-700">
                  {t('festivals.category')}: {getSelectedCategoryName()}
                </span>
                <button
                  onClick={clearCategory}
                  className="text-purple-500 hover:text-purple-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={clearFilters}
            className="w-full"
          >
            {t('festivals.clearAllFilters')}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <HeaderPage
        title={t('navigation.festivals')}
        breadcrumbs={[
          { label: t('common.home'), href: "/" },
          { label: t('navigation.festivals'), href: "/festivals" },
        ]}
      />
      
      <div className="container mx-auto px-4 py-8">
        {/* Mobile Filter Button and Active Filters */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outline"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {t('festivals.filters')}
              {showMobileFilters ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            
            {/* Active filters chips for mobile */}
            <div className="flex gap-2 overflow-x-auto">
              {selectedDestination !== "all" && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                  {getSelectedDestinationName()}
                  <X className="h-3 w-3 ml-1 cursor-pointer" onClick={clearDestination} />
                </span>
              )}
              {selectedCategory !== "all" && filterOptions.categories && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                  {getSelectedCategoryName()}
                  <X className="h-3 w-3 ml-1 cursor-pointer" onClick={clearCategory} />
                </span>
              )}
            </div>
          </div>

          {/* Mobile Search */}
          {/* <Card className="mb-4">
            <CardContent className="p-4">
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t('festivals.searchPlaceholderMobile')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-3 top-3"
                    >
                      <X className="h-4 w-4 text-gray-400" />
                    </button>
                  )}
                </div>
                <Button type="submit">
                  <Search className="h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card> */}

          {/* Mobile Filters Dropdown */}
          {showMobileFilters && (
            <Card className="mb-4">
              <CardContent className="p-4">
                <FilterSidebar />
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar - Filters (Desktop) */}
          <div className="hidden lg:block lg:w-1/4">
            <div className="sticky top-24">
              <FilterSidebar />
            </div>
          </div>

          {/* Right Content - Festivals List */}
          <div className="lg:w-3/4">
            {/* Desktop Search */}
            <div className="hidden lg:block mb-8">
              <form onSubmit={handleSearch} className="relative max-w-md">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder={t('festivals.searchPlaceholderDesktop')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-10 py-6 text-lg"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2"
                  >
                    <X className="h-5 w-5 text-gray-400" />
                  </button>
                )}
              </form>
            </div>

            {/* Results Count */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-foreground dark:text-white">
                    {loading ? t('festivals.loadingFestivals') : `${t('festivals.found')} ${paginationData?.total || 0} ${t('festivals.festivalsCount')}`}
                  </h2>
                  {paginationData && !loading && (
                    <p className="text-foreground dark:text-gray-400">
                      {t('festivals.showing')} {paginationData.from} {t('festivals.to')} {paginationData.to} {t('festivals.of')} {paginationData.total} {t('festivals.results')}
                      {paginationData.current_page && ` (${t('festivals.page')} ${paginationData.current_page} ${t('festivals.of')} ${paginationData.last_page})`}
                    </p>
                  )}
                </div>
                
                {/* Sort Options (optional) */}
                <div className="hidden md:block">
                  <Select defaultValue="popular">
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder={t('festivals.sortBy')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="popular">{t('festivals.mostPopular')}</SelectItem>
                      <SelectItem value="newest">{t('festivals.newestFirst')}</SelectItem>
                      <SelectItem value="oldest">{t('festivals.oldestFirst')}</SelectItem>
                      <SelectItem value="az">{t('festivals.aToZ')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Festivals Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="h-48 w-full rounded-xl" />
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ))}
              </div>
            ) : festivals.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🎵</div>
                <h3 className="text-2xl font-bold mb-2 text-gray-700 dark:text-gray-300">{t('festivals.noFestivalsFound')}</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  {t('festivals.tryAdjustingFilters')}
                </p>
                <Button onClick={clearFilters} size="lg">
                  {t('festivals.clearAllFilters')}
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                  {festivals.map((festival, index) => (
                    <FestivalCard
                      key={festival.id}
                      item={festival}
                    />
                  ))}
                </div>
                
                {/* Pagination */}
                {paginationData && paginationData.last_page > 1 && (
                  <div className="mt-12" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                    <Pagination>
                      <PaginationContent className={locale === 'ar' ? 'flex-row-reverse' : ''}>
                        {renderPaginationLinks()}
                      </PaginationContent>
                    </Pagination>
                    
                    <div className="mt-4 text-center text-sm text-foreground dark:text-gray-400">
                      <p>
                        {t('festivals.page')} {paginationData.current_page} {t('festivals.of')} {paginationData.last_page}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}